import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Upload, CheckCircle2, Loader2, Eye, Image as ImageIcon } from "lucide-react";

const DOCS = [
  { key: "passport", field: "passport_url", label: "Paspor", hint: "JPG / PNG / PDF · max 5 MB", required: true },
  { key: "id_card", field: "id_card_url", label: "KTP", hint: "JPG / PNG · max 5 MB", required: true },
  { key: "family_card", field: "family_card_url", label: "Kartu Keluarga (KK)", hint: "JPG / PNG / PDF · max 5 MB", required: true },
  { key: "vaccine_cert", field: "vaccine_cert_url", label: "Sertifikat Meningitis", hint: "JPG / PNG / PDF · max 5 MB", required: true },
  { key: "marriage_book", field: "marriage_book_url", label: "Buku Nikah", hint: "Wajib jika berpasangan · JPG / PNG / PDF", required: false },
];

const STATUS_META = {
  pending:  { c: "bg-amber-100 text-amber-800",  l: "Menunggu Verifikasi" },
  verified: { c: "bg-emerald-100 text-emerald-800", l: "Diverifikasi" },
  rejected: { c: "bg-red-100 text-red-800", l: "Ditolak" },
};

const ALLOWED = ["image/jpeg", "image/png", "application/pdf"];

function DocRow({ doc, bookingId, storagePath, status, rejectReason, onUploaded }) {
  const [uploading, setUploading] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const inputRef = React.useRef(null);
  const isPdf = storagePath?.endsWith(".pdf");
  const statusMeta = status ? STATUS_META[status] : null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error("Format harus JPG, PNG, atau PDF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File terlalu besar — max 5 MB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/my-bookings/${bookingId}/documents/${doc.key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${doc.label} berhasil diupload`);
      onUploaded(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload gagal");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleView = async () => {
    if (preview) {
      window.open(preview, "_blank");
      return;
    }
    setPreviewLoading(true);
    try {
      const r = await api.get(`/files/${storagePath}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPreview(url);
      window.open(url, "_blank");
    } catch {
      toast.error("Gagal membuka file");
    } finally {
      setPreviewLoading(false);
    }
  };

  React.useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  return (
    <div className="p-4 border border-border rounded-lg" data-testid={`doc-row-${doc.key}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`h-10 w-10 rounded-full shrink-0 flex items-center justify-center ${storagePath ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
            {storagePath ? <CheckCircle2 className="h-5 w-5" /> : isPdf ? <FileText className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <div className="font-medium text-primary flex items-center gap-2 flex-wrap">
              {doc.label}
              {!doc.required && <span className="text-[10px] font-normal text-muted-foreground">(opsional)</span>}
              {statusMeta && (
                <span className={`text-[10px] font-medium uppercase tracking-widest rounded-full px-2 py-0.5 ${statusMeta.c}`} data-testid={`doc-status-${doc.key}`}>
                  {statusMeta.l}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {storagePath ? "Terupload" : doc.hint}
            </div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {storagePath && (
            <button
              onClick={handleView}
              disabled={previewLoading}
              className="text-xs px-3 py-2 rounded-full border border-border hover:bg-muted inline-flex items-center gap-1.5"
              data-testid={`view-${doc.key}`}
            >
              {previewLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />} Lihat
            </button>
          )}
          <label className="text-xs px-3 py-2 rounded-full bg-primary text-white hover:bg-primary/90 cursor-pointer inline-flex items-center gap-1.5" data-testid={`upload-${doc.key}`}>
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {storagePath ? "Ganti" : "Upload"}
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={handleFile} disabled={uploading} />
          </label>
        </div>
      </div>
      {status === "rejected" && rejectReason && (
        <div className="mt-3 rounded-md bg-red-50 border-l-3 border-red-500 p-3 text-xs text-red-900" data-testid={`doc-reject-${doc.key}`}>
          <strong>Alasan penolakan:</strong> {rejectReason}
        </div>
      )}
    </div>
  );
}

export default function DocumentsSection({ booking, onUpdate }) {
  return (
    <div className="mt-6 pt-6 border-t border-border">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <p className="overline">Dokumen Wajib</p>
        <span className="text-xs text-muted-foreground">Hanya Anda &amp; admin BATIK yang dapat mengakses file ini.</span>
      </div>
      <div className="space-y-2">
        {DOCS.map((d) => (
          <DocRow
            key={d.key}
            doc={d}
            bookingId={booking.id}
            storagePath={booking[d.field]}
            status={booking[`${d.key}_status`]}
            rejectReason={booking[`${d.key}_reject_reason`]}
            onUploaded={onUpdate}
          />
        ))}
      </div>
    </div>
  );
}
